import { useEditor, EditorContent, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { MediaImage } from "@/app/Blogs/Components/mediaImageExtension";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import { useCallback, useRef, useEffect, useState } from "react";
import { useFormState } from "@/lib/Components/Form/Hooks/useFormState";
import { useFileUpload } from "@/lib/FileUpload/Hooks/useFileUpload";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { createMediaMutation } from "@/app/Media/GraphQL/mediaMutations";
import { MediaCollection, ModelType } from "@/gql/graphql";
import { Button } from "@/components/catalyst/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  BoldIcon,
  ItalicIcon,
  StrikethroughIcon,
  Heading1Icon,
  Heading2Icon,
  Heading3Icon,
  ListIcon,
  ListOrderedIcon,
  QuoteIcon,
  Link2Icon,
  Link2OffIcon,
  ImageIcon,
  FileTextIcon,
  BlocksIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { CustomBlock } from "@/app/Blogs/Components/customBlockNode";
import { BlockInsertDialog } from "@/app/Blogs/Components/BlockInsertDialog";

type Props = {
  postId?: string;
};

type BlogFormValues = { body_html?: string | null };

export function BlogRichTextEditor({ postId }: Props) {
  const { values, setFieldValue, isEditing } = useFormState<BlogFormValues>();
  const initialHtml = values.body_html ?? "";

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      MediaImage.configure({ inline: false, allowBase64: false }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" },
      }),
      Placeholder.configure({
        placeholder: "Start writing your blog post…",
      }),
      CustomBlock,
    ],
    content: initialHtml,
    editable: isEditing,
    editorProps: {
      attributes: {
        class:
          "prose prose-zinc min-h-[600px] max-w-none focus:outline-none px-4 py-3",
      },
    },
    onUpdate: ({ editor }) => {
      setFieldValue("body_html", editor.getHTML());
    },
  });

  useEffect(() => {
    if (!editor) return;
    if (editor.isEditable !== isEditing) {
      editor.setEditable(isEditing);
    }
  }, [editor, isEditing]);

  // Keep editor in sync if form values change externally (e.g. on load).
  const lastSeenRef = useRef<string>(initialHtml);
  useEffect(() => {
    if (!editor) return;
    if (
      initialHtml !== lastSeenRef.current &&
      initialHtml !== editor.getHTML()
    ) {
      editor.commands.setContent(initialHtml, { emitUpdate: false });
      lastSeenRef.current = initialHtml;
    }
  }, [editor, initialHtml]);

  if (!editor) {
    return (
      <div className="flex h-[640px] items-center justify-center rounded-md border border-zinc-200 bg-zinc-50 text-sm text-zinc-500">
        Loading editor…
      </div>
    );
  }

  return (
    <div className="flex flex-col rounded-md border border-zinc-200 bg-white">
      {isEditing ? <Toolbar editor={editor} postId={postId} /> : null}
      <EditorContent editor={editor} className="overflow-auto" />
    </div>
  );
}

function Toolbar({ editor, postId }: { editor: Editor; postId?: string }) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const pdfInputRef = useRef<HTMLInputElement | null>(null);
  const { mutateAsync: upload, isPending: isUploading } = useFileUpload();
  const { mutateAsync: createMedia } = useGqlMutation(createMediaMutation);
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);

  const handleInsertLink = useCallback(() => {
    const prev = editor.getAttributes("link").href as string | undefined;
    const url = window.prompt("URL", prev ?? "https://");

    if (url === null) return;

    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }, [editor]);

  const handleImageUpload = useCallback(
    async (file: File) => {
      if (!postId) {
        window.alert(
          "Save the post first — image uploads need a post id to attach to.",
        );
        return;
      }

      const [uploaded] = await upload([file]);
      if (!uploaded) return;

      const res = await createMedia({
        input: {
          file_name: uploaded.file.name,
          objectId: postId,
          objectType: ModelType.BlogPost,
          collectionName: MediaCollection.BlogBodyImages,
          key: uploaded.filePath,
        },
      });

      editor
        .chain()
        .focus()
        .setImage({
          src: res.createMedia.url,
          alt: uploaded.file.name,
          "data-media-id": res.createMedia.id,
        } as { src: string; alt: string; "data-media-id": string })
        .run();
    },
    [createMedia, editor, postId, upload],
  );

  const handlePdfUpload = useCallback(
    async (file: File) => {
      if (!postId) {
        window.alert(
          "Save the post first — PDF uploads need a post id to attach to.",
        );
        return;
      }

      const [uploaded] = await upload([file]);
      if (!uploaded) return;

      const res = await createMedia({
        input: {
          file_name: uploaded.file.name,
          objectId: postId,
          objectType: ModelType.BlogPost,
          collectionName: MediaCollection.Pdf,
          key: uploaded.filePath,
        },
      });

      editor
        .chain()
        .focus()
        .insertContent({
          type: "paragraph",
          content: [
            {
              type: "text",
              text: uploaded.file.name,
              marks: [
                {
                  type: "link",
                  attrs: { href: res.createMedia.url, target: "_blank" },
                },
              ],
            },
          ],
        })
        .run();
    },
    [createMedia, editor, postId, upload],
  );

  return (
    <TooltipProvider delayDuration={200}>
      <div className="sticky top-0 z-10 flex flex-wrap items-center gap-1 border-b border-zinc-200 bg-zinc-50/80 px-2 py-1 backdrop-blur">
        <ToolbarButton
          active={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}
          ariaLabel="Bold"
        >
          <BoldIcon className="size-4" />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          ariaLabel="Italic"
        >
          <ItalicIcon className="size-4" />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive("strike")}
          onClick={() => editor.chain().focus().toggleStrike().run()}
          ariaLabel="Strikethrough"
        >
          <StrikethroughIcon className="size-4" />
        </ToolbarButton>

        <Divider />

        <ToolbarButton
          active={editor.isActive("heading", { level: 1 })}
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 1 }).run()
          }
          ariaLabel="Heading 1"
        >
          <Heading1Icon className="size-4" />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive("heading", { level: 2 })}
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 2 }).run()
          }
          ariaLabel="Heading 2"
        >
          <Heading2Icon className="size-4" />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive("heading", { level: 3 })}
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 3 }).run()
          }
          ariaLabel="Heading 3"
        >
          <Heading3Icon className="size-4" />
        </ToolbarButton>

        <Divider />

        <ToolbarButton
          active={editor.isActive("bulletList")}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          ariaLabel="Bulleted list"
        >
          <ListIcon className="size-4" />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive("orderedList")}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          ariaLabel="Numbered list"
        >
          <ListOrderedIcon className="size-4" />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive("blockquote")}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          ariaLabel="Blockquote"
        >
          <QuoteIcon className="size-4" />
        </ToolbarButton>

        <Divider />

        <ToolbarButton
          active={editor.isActive("link")}
          onClick={handleInsertLink}
          ariaLabel="Link"
        >
          <Link2Icon className="size-4" />
        </ToolbarButton>
        <ToolbarButton
          disabled={!editor.isActive("link")}
          onClick={() => editor.chain().focus().unsetLink().run()}
          ariaLabel="Remove link"
        >
          <Link2OffIcon className="size-4" />
        </ToolbarButton>

        <ToolbarButton
          disabled={isUploading || !postId}
          onClick={() => fileInputRef.current?.click()}
          ariaLabel="Insert image"
        >
          <ImageIcon className="size-4" />
        </ToolbarButton>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = "";
            if (file) void handleImageUpload(file);
          }}
        />

        <ToolbarButton
          disabled={isUploading || !postId}
          onClick={() => pdfInputRef.current?.click()}
          ariaLabel="Insert PDF"
        >
          <FileTextIcon className="size-4" />
        </ToolbarButton>
        <input
          ref={pdfInputRef}
          type="file"
          accept="application/pdf"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = "";
            if (file) void handlePdfUpload(file);
          }}
        />

        <Divider />

        <ToolbarButton
          onClick={() => setBlockDialogOpen(true)}
          ariaLabel="Insert block"
        >
          <BlocksIcon className="size-4" />
        </ToolbarButton>
        <BlockInsertDialog
          open={blockDialogOpen}
          onClose={setBlockDialogOpen}
          onInsert={(name, props) => {
            editor.commands.insertCustomBlock({
              name,
              props: JSON.stringify(props),
            });
          }}
        />
      </div>
    </TooltipProvider>
  );
}

function Divider() {
  return <div className="mx-1 h-5 w-px bg-zinc-200" />;
}

function ToolbarButton({
  active,
  disabled,
  onClick,
  ariaLabel,
  children,
}: {
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  ariaLabel: string;
  children: React.ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          plain
          onClick={onClick}
          disabled={disabled}
          aria-label={ariaLabel}
          aria-pressed={active}
          className={cn(
            "size-8 p-0 text-zinc-700 hover:bg-zinc-200/60",
            active && "bg-zinc-200 text-zinc-900",
            disabled && "opacity-40",
          )}
        >
          {children}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{ariaLabel}</TooltipContent>
    </Tooltip>
  );
}
